import { axios } from './axiosClient';
import { validate } from '@/utils/errors/helpers';
import { getCompetitorTypesSchema } from '@/types/schemas/traders/getCompetitorTypes';
import type {
  Competitor,
  CompetitorType,
  Customer,
  Representative,
  Trader,
  TraderSite,
  TraderSiteWithBaseCustomer,
} from '@/types/Traders';
import {
  getAllCompetitorsSchema,
  getCompetitorSchema,
  getCompetitorsSchema,
} from '@/types/schemas/traders/getCompetitors';
import type { TableFilterData } from '@/types/Base';
import {
  getCustomersByIDSchema,
  getCustomersWithConditionsSchema,
} from '@/types/schemas/traders/getCustomers';
import {
  findTraderSchema,
  getTradersSchema,
  traderSchema,
} from '@/types/schemas/traders/getTraders';
import {
  getTraderSitesSchema,
  getTraderSitesWithCustomerSchema,
  traderSiteSchema,
} from '@/types/schemas/traders/getTraderSites';
import { getTraderDetailsSchema } from '@/types/schemas/traders/getTraderDetailsById';
import { getTaxOfficesSchema } from '@/types/schemas/traders/getTaxOffeces';
import {
  getRepresentativesWithConditionsSchema,
  getRepresentativesByIDSchema,
} from '@/types/schemas/traders/getRepresentatives';

export async function getCompetitorTypes() {
  const response = await axios.get('/v2/traders/competitor-types');

  return validate(getCompetitorTypesSchema, response.data.data);
}

export async function removeCompetitorTypesByIds(ids: string[]) {
  const response: any = await axios.post('/v2/traders/competitor-types/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function addOrEditCompetitorType(competitorType: CompetitorType) {
  const response: any = await axios.post('v2/traders/competitor-types', competitorType);
  if (response.status === 201) return true;
  return false;
}

export async function getCompetitors(): Promise<Partial<Competitor>[]> {
  const response = await axios.get('/v2/traders/competitors');
  return validate(getAllCompetitorsSchema, response.data.data);
}

export async function removeCustomersByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/traders/customers/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getCustomersWithConditions({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  groupItemId,
  traderId,
  pricingPolicyId,
  withoutPricingPolicy,
  isActive = true,
}: TableFilterData & {
  groupItemId?: string;
  traderId?: string;
  pricingPolicyId?: string;
  withoutPricingPolicy?: boolean;
  isActive?: boolean;
}): Promise<{
  data: Customer[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/traders/customers/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn,
      sortAscending,
      groupItemId,
      traderId,
      pricingPolicyId,
      withoutPricingPolicy,
      isActive,
    }
  );

  const valid = validate(getCustomersWithConditionsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getCustomerById(id: string): Promise<Customer | null> {
  const response = await axios.get(`/v2/traders/customers/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });

  if (response.status === 404) return null;

  const customerResponse = validate(getCustomersByIDSchema, response.data);
  return customerResponse as Customer;
}

export async function addOrEditCustomer(customer: Customer) {
  const response: any = await axios.post('v2/traders/customer', customer);
  if (response.status === 201) return true;
  return false;
}

export async function getTradersWithConditions({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  isActive,
}: TableFilterData): Promise<{ data: Partial<Trader>[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(`/v2/traders/search?take=${itemsPerPage}&skip=${skip}`, {
    searchText: query,
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive,
  });

  const valid = validate(getTradersSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getTraderById(id: string): Promise<Trader | null> {
  const response = await axios.get(`/v2/traders/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });

  if (response.status === 404) return null;

  const traderResponse = validate(traderSchema, response.data);
  return traderResponse as Trader;
}

export async function getTraderDetailsById(traderId: string): Promise<Trader | null> {
  const response = await axios.get(`/v2/traders/${traderId}/details`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });
  if (response.status === 404) return null;
  const traderResponse = validate(getTraderDetailsSchema, response.data);
  return traderResponse as Trader;
}

export async function findTraderByTaxNo(taxNo: string): Promise<Trader | null> {
  const response = await axios.get(`/v2/traders/taxno-find/${taxNo}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });
  if (response.status === 404) return null;
  const traderResponse = validate(findTraderSchema, response.data);
  return traderResponse as Trader;
}

export async function addOrEditTrader(trader: Trader) {
  const response: any = await axios.post('v2/traders', trader);
  if (response.status === 201) return true;
  return false;
}

export async function removeTradersByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/traders/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getTraderSitesWithConditions({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  traderId,
  groupItemId,
  grouped,
  id,
  excludeIDs,
  withCustomers = false,
  isActive,
}: TableFilterData & {
  traderId?: string;
  groupItemId?: string;
  grouped?: boolean;
  id?: string;
  withCustomers?: boolean;
}): Promise<{
  data: TraderSite[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  let url = `/v2/traders/trader-sites/search?take=${itemsPerPage}&skip=${skip}`;
  if (traderId) url += `&traderId=${traderId}`;
  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    groupItemId,
    grouped,
    id,
    withCustomers,
    excludeIDs,
    isActive,
  });
  const valid = validate(getTraderSitesSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getRouteAvailableTraderSitesWithConditions({
  routeId,
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
}: TableFilterData & { routeId: string }): Promise<{
  data: TraderSiteWithBaseCustomer[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const url = `/v2/traders/routeTraderSites/available-sites?take=${itemsPerPage}&skip=${skip}&routeId=${routeId}`;
  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
  });
  const valid = validate(getTraderSitesWithCustomerSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getTraderSiteById(id: string): Promise<TraderSite | null> {
  const response = await axios.get(`/v2/traders/trader-sites/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });

  if (response.status === 404) return null;

  const traderSiteResponse = validate(traderSiteSchema, response.data);
  return traderSiteResponse as TraderSite;
}

export async function removeTraderSitesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/traders/trader-sites/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function editTraderSite(traderSite: TraderSite) {
  const response: any = await axios.post('v2/traders/trader-sites', traderSite);
  if (response.status === 201) return true;
  return false;
}

export async function getCompetitorsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  isActive,
}: TableFilterData): Promise<{ data: Competitor[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/traders/competitors/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn: sortColumn,
      sortAscending: sortAscending,
      isActive: isActive,
    }
  );

  const valid = validate(getCompetitorsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function removeCompetitorsByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/traders/competitors/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getCompetitorById(id: string): Promise<Competitor | null> {
  const response = await axios.get(`/v2/traders/competitors/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });

  if (response.status === 404) return null;

  const competitorResponse = validate(getCompetitorSchema, response.data);
  return competitorResponse as Competitor;
}

export async function addOrEditCompetitor(competitor: Competitor) {
  const response: any = await axios.post('v2/traders/competitor', competitor);
  if (response.status === 201) return true;
  return false;
}

export async function getTaxOffices() {
  const result = await axios.get('v2/traders/tax-offices');
  return validate(getTaxOfficesSchema, result.data.data);
}

export async function getTraderSitesWithCustomers({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  excludePairs,
  rangeId,
}: TableFilterData & { excludePairs: string[] | null; rangeId?: string }): Promise<{
  data: TraderSiteWithBaseCustomer[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const url = `/v2/traders/trader-sites/customer-sites?take=${itemsPerPage}&skip=${skip}`;
  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    excludePairs,
    rangeId,
  });
  const valid = validate(getTraderSitesWithCustomerSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getCustomerHtmlTabs(params: {
  enumValue: number;
  customerBoid?: string;
  traderSiteBoid?: string;
  materialBoid?: string;
  salesManBoid?: string;
}): Promise<string | null> {
  const response = await axios.post(
    `/v2/traders/customers/getHtmlTabs`,
    {
      enum: params.enumValue,
      customerBoid: params.customerBoid,
      traderSiteBoid: params.traderSiteBoid,
      materialBoid: params.materialBoid,
      salesManBoid: params.salesManBoid,
    },
    {
      validateStatus: (status: number) => status === 400 || status === 404 || status == 200,
    }
  );

  if (response.status === 404) return null;

  return response.data;
}

//Representatives
export async function getRepresentativesWithConditions({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  traderId,
  isActive,
}: TableFilterData & {
  traderId?: string;
}): Promise<{
  data: Representative[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/traders/representatives/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn,
      sortAscending,
      traderId,
      isActive,
    }
  );
  const valid = validate(getRepresentativesWithConditionsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function removeRepresentativesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/traders/representatives/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getRepresentativeById(id: string): Promise<Representative | null> {
  const response = await axios.get(`/v2/traders/representatives/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });

  if (response.status === 404) return null;

  const representativeResponse = validate(getRepresentativesByIDSchema, response.data);
  return representativeResponse as Representative;
}

export async function addOrEditRepresentative(representative: Representative) {
  const response: any = await axios.post('v2/traders/representative', representative);
  if (response.status === 201) return true;
  return false;
}
